import { ChevronRight, Close,ExpandMore, FilterListRounded } from '@mui/icons-material';
import { Box,IconButton, InputAdornment, TextField } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation,useNavigate } from 'react-router-dom';

import type { MenuTreeNodeVO } from '../../../api/cm';

interface MenuTreeNodeVOWithPath extends MenuTreeNodeVO {
  path?: string;
  displayNameKey?: string;
}

export const MENU_CONFIG = [
  {
    id: "1",
    path: "/projects",
    displayNameKey: "menu.ProjectList",
  },
  {
    id: "2",
    path: "/projects/new",
    hiddenInSideMenu: true,
    displayNameKey: "project.newProject",
  },
  {
    id: "3",
    path: "/projects/detail",
    hiddenInSideMenu: true,
    displayNameKey: "project.projectDetail",
  },
  {
    id: "4",
    path: "/artifacts",
    displayNameKey: "menu.artifacts",
  },
  {
    id: "5",
    path: "/artifacts/detail",
    hiddenInSideMenu: true,
    displayNameKey: "project.artifactDetail",
  },
  {
    id: "6",
    path: "/tools",
    hiddenInSideMenu: true,
    displayNameKey: "menu.tools",
  }
]

const menuData = MENU_CONFIG.filter((item) => !item.hiddenInSideMenu);

const SideMenu: React.FC = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname;
    for (const item of menuData) {
      if (['/projects', '/projects/new', '/projects/detail'].includes(currentPath)) {
        setSelectedItemId('1');
      } else if (item.path === currentPath) {
        setSelectedItemId(item.id);
      }
    }
  }, [location.pathname]);

  const handleMenuItemClick = useCallback((item: MenuTreeNodeVOWithPath) => {
    const pathString = item.path;
    if (!pathString || item.children?.length) return;
    if (selectedItemId === item.id && !['1', '4'].includes(selectedItemId)) return;
    navigate(pathString);
  }, [navigate, selectedItemId]);

  const renderTree = useCallback((items: MenuTreeNodeVOWithPath[]) => {
    return items.map((item) => (
      <TreeItem
        key={item.id as string}
        itemId={item.id as string}
        data-testid={`side-menu-item-${item.id}`}
        label={t(item.displayNameKey as string)}
        onClick={(event) => {
          event.stopPropagation();
          handleMenuItemClick(item)
        }}
        sx={{
          '& .MuiTreeItem-iconContainer': {
            width: 0,
          },
          '&': {
            marginBottom: '10px',
          },
          '& .MuiTreeItem-content[data-selected]': {
            backgroundColor: 'transparent',
            color: '#295ef1',
          }
        }}
      >
        {item.children ? renderTree(item.children) : null}
      </TreeItem>
    ));
  }, [handleMenuItemClick, t]);

  const filterMenuItems = (items: MenuTreeNodeVOWithPath[], search: string): MenuTreeNodeVOWithPath[] => {
    if (!search.trim()) return items;

    return items.filter(item => {
      const currentMatches = t(item?.displayNameKey as string)?.toLowerCase().includes(search.toLowerCase());
      return currentMatches;
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const filteredMenuData = filterMenuItems(menuData, searchTerm);

  return (
    <Box className="side-menu" sx={{ width: '100%' }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={t('common.menuSearch')}
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        sx={{ mb: 2 }}
        autoComplete="off"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <FilterListRounded color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }
        }}
      />
      <SimpleTreeView
        selectedItems={selectedItemId}
        slots={{
          collapseIcon: ExpandMore,
          expandIcon: ChevronRight,
        }}
      >
        {renderTree(filteredMenuData)}
      </SimpleTreeView>
    </Box>
  );
};

export default SideMenu;